import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, Controller } from 'react-hook-form';
import type { Questionnaire } from '@/typings/Questionnaire';
import PlusCircle from '@/components/Icons/PlusCircle';
import MinusCircle from '@/components/Icons/MinusCircle';
import { Table, TableRow, TBody, TD, TH, THead } from '@/components/DesignLibrary/Table';
import { useFeatureFlag } from '@providers/FeatureFlags';
import SearchInput from '@/components/FormFields/SearchInput';
import PaginationControls from '@components/DesignLibrary/PaginationControls';
import Buttons from '@/components/Buttons';
import CustomSelect from '@/components/FormFields/CustomSelect';
import PencilSimple from '@/components/Icons/PencilSimple';
import Eye from '@/components/Icons/Eye';
import { useQuestionnaireContext } from '@/components/Questionnaire/QuestionnaireProvider';
import { nanoid } from 'nanoid';
import { Checkbox, Switch } from '@components/DesignLibrary/Atoms';
import ArrowElbowDownRight from '@components/Icons/ArrowElbowDownRight';
import ProgressIndicator from '@components/ProgressIndicator';
import EditListItemModal from '@components/Questionnaire/form/List/EditListItemModal';
import { uniq } from 'lodash';
import removeNullish from '@/helpers/removeNullish';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import TrashSimple from "@components/Icons/TrashSimple";
import { useEventsContext } from '@/providers/Events';
import Shimmer from '@/components/Shimmer';
import isEmptyObject from '../FormField/utils/isEmptyObject';

import NonListFormField from '../FormField/NonListFormField';
import getDisplayValue from './getDisplayValue';
import isSubFieldVisible from './isSubFieldVisible';
import evaluateListFilterExpression from './evaluateListFilterExpression';
import getDeep from './getDeep';
import s from './List.module.scss';
import DeletedFieldsModal from './DeletedFieldsModal';
import EditListItemModalOnSave from './EditListItemModalOnSave';

type ListProps = {
  field: Questionnaire.FieldList;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules: any;
  isPreview?: boolean;
  isEditor?: boolean;
  fieldsAutofilling?: string[];
};

interface SortField {
  [key: string]: string | number | boolean | null | undefined;
}

type DynamicSortFunction = (a: SortField, b: SortField) => number;

function dynamicSort(fields: string[]): DynamicSortFunction {
  return (a: SortField, b: SortField): number => {
    let result = 0;

    fields.some((field) => {
      const aVal = a[field];
      const bVal = b[field];

      // Handle undefined/null
      if (aVal == null && bVal != null) {
        result = -1;
        return true; // Exit loop
      }
      if (aVal != null && bVal == null) {
        result = 1;
        return true; // Exit loop
      }
      if (aVal == null && bVal == null) {
        return false; // Continue loop
      }

      // Compare strings
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        result = aVal.localeCompare(bVal);
        return result !== 0; // Exit loop if not equal
      }

      // Compare numbers or booleans
      if (typeof aVal === 'number' || typeof aVal === 'boolean') {
        result = (aVal as number) - (bVal as number);
        return result !== 0; // Exit loop if not equal
      }

      // Fallback: compare as strings
      result = String(aVal).localeCompare(String(bVal));
      return result !== 0; // Exit loop if not equal
    });

    return result;
  };
}

const PAGINATION_OPTIONS = [25, 50, 100].map(value => ({ title: `${value} items per page`, value: String(value) }));

type RowData =
  & Record<string, unknown>
  & {
    id: string;
    _id: string;
    _associatedWithId?: string;
  };

export type DeletedRow = Omit<RowData, 'id'> & {
  _id: string;
};

const List: React.FC<ListProps> = ({ field: incomingField, control, rules, isPreview, isEditor, fieldsAutofilling }) => {
  const [endData, setEndData] = useState<RowData[]>([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<25 | 50 | 100>(25);
  const [modalOpen, setModalOpen] = useState<number | undefined>(undefined);
  const [checkedRows, setCheckedRows] = useState<string[]>([]);
  const [deletedValues, setDeletedValues] = useState<DeletedRow[]>([]);
  const [removedItemsModalOpen, setRemovedItemsModalOpen] = useState(false);
  const [currentSortDirection, setCurrentSortDirection] = useState<{ column: string; direction: 'asc' | 'desc' } | null>(null);
  const [showDuplicates, setShowDuplicates] = useState(false);

  const useEditableInTableView = useFeatureFlag('questionnaire-editable-table-view');
  const questionnaireEditListItemOnSave = useFeatureFlag('questionnaire-edit-list-item-on-save');
  const {
    formMethods,
    fieldsById,
    fieldsByKey,
    checkFieldValidity,
    setValue,
    questionnaireId,
  } = useQuestionnaireContext();
  const fullField = fieldsByKey[incomingField.key];

  const {
    data: deletedQuestionnaireResponses,
    fetchingState:{isFetching:loadingDeletedResponses},
    getData: getDeletedQuestionnaireResponsesFn,
  } = useNoodleApi(tsClient.questionnaires.getDeletedQuestionnaireResponses);

  const { addListener, removeListener } = useEventsContext();

  const linkSourceFieldKey = useMemo((): string => {
    const fieldTemplateId = fullField?.linkedFields?.find(f => f.fieldTemplateId === fullField?.id)?.linkedFieldTemplateId;
    return fieldTemplateId ? (fieldsById[fieldTemplateId]?.key || fullField.key) : fullField?.key;
  }, [fullField, fieldsById]);

  const linkedField = fieldsByKey[linkSourceFieldKey];

  const inMainList = fullField?.key === linkSourceFieldKey;

  const { fields: tmpFields, append, remove } = useFieldArray({
    control,
    name: linkSourceFieldKey,
  });

  const fields = tmpFields as unknown as RowData[];

  const handleCheckboxChange = (rowId: string, checked: boolean): void => {
    const row = fields.find(item => item.id === rowId);
    if (!row) {
      return;
    }

    setCheckedRows(prev => {
      const updated = new Set(prev);
      if (checked) {
        updated.add(rowId);
        if (!row._associatedWithId) {
          fields
            .filter(item => item._associatedWithId === row._id)
            .forEach(child => updated.add(child.id));
        }
      } else {
        updated.delete(rowId);

        if (!row._associatedWithId) {
          fields
            .filter(item => item._associatedWithId === row._id)
            .forEach(child => updated.delete(child.id));
        }
      }
      return Array.from(updated);
    });
  };

  const initializeSubFields = useCallback(() => {
    const newItem: Record<string, unknown> = {
      _id: nanoid(),
    };
    (fullField?.listItemTemplates ?? []).forEach(subField => {
      newItem[subField.key] = undefined;
    });
    return newItem;
  }, [fullField?.listItemTemplates]);

  useEffect(() => {
    const endDataLocal = fields?.filter(item => {
      if(fullField?.listFilterExpression) return evaluateListFilterExpression(item, fullField.listFilterExpression);
      return true;
    });
    setEndData(endDataLocal ?? []);
  },[fields,fullField]);

  const foundFields = useMemo(() => {
    if (search) {
      return fields.filter(item =>
        Object.entries(item).some(([key, value]) => {
          const subField = fullField?.listItemTemplates?.find(f => f.key === key);
          if (!subField) return false;
          const displayValue = getDisplayValue(subField, value);
          return displayValue.toLowerCase().includes(search.toLowerCase());
        }),
      );
    }
    return endData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [fields, search, endData, currentPage, itemsPerPage, fullField]);

  const removeListItem = useCallback((index: number): void => {
    if(fields[index]) {
      setDeletedValues([...deletedValues, fields[index]]);
    }
    remove(index);
    formMethods.unregister(fullField.listItemTemplates?.map((subField) => `${fullField.key}[${index}].${subField.key}`));
  }, [remove, formMethods, fullField, deletedValues]);

  const removeSelectedItems = (): void => {
    if (window.confirm(`Are you sure you want to remove ${checkedRows.length > 1 ? 'these' : 'this'} ${checkedRows.length} item${checkedRows.length > 1 ? 's' : ''}?`)) {
      const indicesToRemove = checkedRows
        .map(rowId => fields.findIndex(item => item.id === rowId))
        .filter(index => index !== -1);
      indicesToRemove.sort((a, b) => b - a);

      const fieldsToDelete: RowData[]=[];
      indicesToRemove.forEach(index => {
        fieldsToDelete.push(fields[index]);
        remove(index);
      });
      setDeletedValues([...deletedValues, ...fieldsToDelete]);
      setCheckedRows([]);
    }
  };

  useEffect(() => {
    getDeletedQuestionnaireResponsesFn({ listFieldTemplateKey:linkSourceFieldKey, questionnaireId });
  },[questionnaireId,linkSourceFieldKey]);

  useEffect(() => {
    const listener = addListener('questionnaireResponseRestored', () => {
      getDeletedQuestionnaireResponsesFn({ listFieldTemplateKey:linkSourceFieldKey, questionnaireId });
    });

    return () => removeListener('questionnaireResponseRestored', listener);
  }, [addListener, removeListener]);

  useEffect(() => {
    if(!deletedQuestionnaireResponses?.length) {
      setDeletedValues([]);
      return;
    }

    const formattedValues = deletedQuestionnaireResponses.map(item => {
      const obj: DeletedRow = {
        _associatedWithId: item.associatedWithId,
        _existsInDb: true,
        _id: item.id,
      };
      item.listResponses?.forEach(listItem => {
        obj[listItem.fieldTemplate.key] = listItem.value;
      });
      return obj;
    });
    const filteredValues = formattedValues?.filter(item => {
      if(fullField?.listFilterExpression) return evaluateListFilterExpression(item, fullField.listFilterExpression);
      return true;
    });
    setDeletedValues(filteredValues);

    // If there are only deleted entries, make sure list gets set to empty array instead of undefined
    if (formMethods.getValues(linkSourceFieldKey) === undefined) {
      setValue({ key: linkSourceFieldKey, source: 'unknown', value: [] });
    }
  }, [deletedQuestionnaireResponses]);

  const renderTableRow = useCallback((item: Record<'id', string>, parentId?: string, subSections?: string[], isDuplicateField = false): React.ReactElement => {
    const originalFieldsIndex = fields.findIndex(f => f.id === item.id);

    const isChildDisabled = isDuplicateField && parentId
      ? checkedRows.includes(parentId)
      : false;

    const requiredSubFields = fullField.listItemTemplates ?? [];

    const getReferencedListOptions = (subField: Questionnaire.Field): [] => {
      if (subField.referencedList?.isReferencingExistingList) {
        const referencedListItems = formMethods.getValues(subField.referencedList.fieldKey) || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return referencedListItems.map((rlItem: Record<string, any>) => ({
          key: rlItem._id,
          label: subField.referencedList?.subFieldKey ? rlItem[subField.referencedList.subFieldKey] : rlItem._id,
          value: rlItem._id,
        }));
      }
      return [];
    };

    const hasIncompleteRequiredFields = requiredSubFields.some(subField => {
      if (!subField.isRequired) {
        return false;
      }

      const itemValues = formMethods.getValues(`${linkSourceFieldKey}[${originalFieldsIndex}]`) as unknown;

      if (!isSubFieldVisible(subField, itemValues)) {
        return false;
      }

      const fieldName = `${linkSourceFieldKey}[${originalFieldsIndex}].${subField.key}`;
      const error = getDeep(formMethods.formState.errors, fieldName);
      const value = getDeep(itemValues, subField.key);

      return error !== undefined || !checkFieldValidity(subField, value);
    });

    const isRowChecked = checkedRows.includes(item.id);

    const listItemsTemplatesGroupedBySubsection = fullField.listItemTemplates?.filter(template => template.isViewableInTableView)
      .reduce((acc, template) => {
        if (template.listSubsection) {
          acc[template.listSubsection] = acc[template.listSubsection] || [];
          acc[template.listSubsection].push(template);
        }
        return acc;
      }, {} as Record<string, Questionnaire.Field[]>);

    return (
      <TableRow key={item.id} title={hasIncompleteRequiredFields ? 'Item has incomplete required fields' : undefined}>
        <TD className={s.editButtonCell}>
          <div className={s.editButtonCellBg} />
          {!isPreview && <Checkbox size='sm' label='' isChecked={isRowChecked} disabled={isChildDisabled} onChange={(checked) => handleCheckboxChange(item.id, checked)} className={s.editCheckbox} />}
          <Buttons
            isWrapper
            className={s.editButton}
            onClick={() => setModalOpen(originalFieldsIndex)}
          >
            {!isPreview && !fullField.disabled
              ? <>
                {hasIncompleteRequiredFields && <div className={s.incompleteRequiredFieldsIndicator} />}
                <PencilSimple size={16} />
              </>
              : <Eye size={16} />}
          </Buttons>
        </TD>
        <>
          {listItemsTemplatesGroupedBySubsection && !isEmptyObject(listItemsTemplatesGroupedBySubsection) && subSections
            ? <>
              {listItemsTemplatesGroupedBySubsection && subSections?.map(subSection => <TD key={subSection}>
                {listItemsTemplatesGroupedBySubsection[subSection]?.map((subSectionField) => (
                  <div key={subSectionField.id} className={s.subSectionWrapper}>
                    <Controller
                      name={`${linkSourceFieldKey}[${originalFieldsIndex}].${subSectionField.key}`}
                      control={control}
                      render={({ field: { value } }) => (
                        <>
                          {value
                            && (subSectionField?.referencedList?.isReferencingExistingList
                              ? getDisplayValue({ ...subSectionField, options: getReferencedListOptions(subSectionField) }, value)
                              : getDisplayValue(subSectionField, value)) !== "—"
                            ? <>
                              <p className={s.subSectionFieldLabel}>{subSectionField.label}</p>
                              <p className='caption'>
                                {
                                  subSectionField?.referencedList?.isReferencingExistingList
                                    ? getDisplayValue({ ...subSectionField, options: getReferencedListOptions(subSectionField) }, value)
                                    : getDisplayValue(subSectionField, value)
                                }
                              </p>
                            </>
                            : null}
                        </>
                      )}
                    />
                  </div>
                ))}
              </TD>)}
            </>
            : <>
              {fullField.listItemTemplates?.filter(subField => subField.isViewableInTableView)
                .map((subField, index) => (
                  <TD key={subField.id}>
                    <div className={s.tableColumn}>
                      {index === 0 && isDuplicateField && (
                        <div style={{ height: 16, paddingRight: 8 }}>
                          <ArrowElbowDownRight size={16} />
                        </div>
                      )}
                      <span className={s.cell}>
                        {(subField.isEditableInTableView && useEditableInTableView)
                          ? <NonListFormField
                            key={subField.id}
                            listField={fullField}
                            index={index}
                            field={{
                              ...subField,
                              description: '',
                              key: `${linkSourceFieldKey}[${originalFieldsIndex}].${subField.key}`,
                              label: '',
                              linkedFields: fieldsByKey[subField.key]?.linkedFields?.filter((f) => f.linkedFieldTemplateIndex === index),
                              listItemTemplates: Object.values(fieldsByKey).filter((f) => f.listFieldTemplateId === subField.id),
                              ...subField.type === 'single-select' && { native: true, placeholder: 'Select an option', type: 'selection' },
                              ...subField.type === 'currency' && { nativeSelect: true },
                            }}
                            isPreview={isPreview}
                            control={control}
                            rules={rules[subField.key]}
                            fieldsAutofilling={fieldsAutofilling}
                          />
                          : <Controller
                            name={`${linkSourceFieldKey}[${originalFieldsIndex}].${subField.key}`}
                            control={control}
                            render={({ field: { value } }) => (
                              <span style={value ? undefined : { color: 'var(--color-gray-25)' }}>
                                {
                                  subField?.referencedList?.isReferencingExistingList
                                    ? getDisplayValue({ ...subField, options: getReferencedListOptions(subField) }, value)
                                    : getDisplayValue(subField, value)
                                }
                              </span>
                            )}
                          />}
                      </span>
                    </div>
                  </TD>
                ))}
            </>}
        </>
        {!isPreview && !fullField.disabled && inMainList && (
          <TD>
            <button
              className={s.removeItemButtonRow}
              type="button"
              onClick={() => {
                if (window.confirm('Are you sure you want to remove this item?')) {
                  removeListItem(originalFieldsIndex);
                  if (foundFields.length === 1 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                  }
                }
              }}
            >

              <MinusCircle weight="fill" /> Remove Item
            </button>
          </TD>
        )}
      </TableRow>
    );
  }, [
    inMainList,
    isPreview,
    rules,
    useEditableInTableView,
    checkFieldValidity,
    linkSourceFieldKey,
    foundFields.length,
    removeListItem,
    currentPage,
    setCurrentPage,
    checkedRows,
    control,
    fields,
    fieldsAutofilling,
    fieldsByKey,
    fullField,
    formMethods,
  ]);

  const listSubsections = useMemo(
    () => uniq(fullField?.listItemTemplates?.sort((a, b) => a.rank - b.rank)?.map((subField) => subField.listSubsection).filter(removeNullish)),
    [fullField]);

  if (!fullField) {
    return <ProgressIndicator />;
  }

  return (
    <div className={s.listContainer}>
      <div className={s.listHeader}>
        <div>
          <label className="body-sm-bold">{fullField.label}</label>
          {linkedField.view === 'table' && !isPreview && <Switch name='showDuplicates' label={`${showDuplicates ? 'Hide' : 'Show'} duplicates`} onChange={() => setShowDuplicates(prev => !prev)} isChecked={showDuplicates} />}
        </div>
        {fullField.description && <p className="caption">{fullField.description}</p>}
      </div>
      {linkedField.view === 'table' && !isEditor && fullField?.listItemTemplates
        ? (
          <div className={s.tableElements}>
            <div style={{display:'flex'}}>
              <div style={{flex:1}}>
                <SearchInput label="Search items" hideLabel setSearch={setSearch} placeholder="Search items" className={s.search} />
              </div>
              {
                loadingDeletedResponses && !deletedValues?.length
                  ?<div className={s.removedItemBtnContainer}>
                    <Shimmer className={[s.shimmer, s.viewRemovedItemsButton].join(" ")} />
                  </div>
                  :null
              }
              {deletedValues?.length && !loadingDeletedResponses
                ?<div className={s.removedItemBtnContainer}>
                  <button className={s.viewRemovedItemsButton} onClick={() => setRemovedItemsModalOpen(true)}>
                    <TrashSimple weight="fill" /> Removed Items {`(${deletedValues?.length})`}
                  </button></div>
                :null}
            </div>
            <Table className={s.table}>
              <THead>
                <TableRow>
                  {foundFields.length > 0 && <TH />}
                  {listSubsections.length > 0
                    ? <>
                      {listSubsections.map(subsection => (
                        <TH
                          key={subsection}
                          columnId={subsection}
                          sort={ subsection
                            ? {
                              currentSortByField: currentSortDirection?.column || '',
                              currentSortDirection: currentSortDirection?.direction ?? 'asc',
                              onSort(columnId, direction) {
                                const listItemsTemplatesGroupedBySubsection = fullField.listItemTemplates
                                  ?.filter(template => template.isViewableInTableView)
                                  .reduce((acc, template) => {
                                    if (template.listSubsection) {
                                      acc[template.listSubsection] = acc[template.listSubsection] || [];
                                      acc[template.listSubsection].push(template);
                                    }
                                    return acc;
                                  }, {} as Record<string, Questionnaire.Field[]>);

                                if(listItemsTemplatesGroupedBySubsection) {
                                  const keys = listItemsTemplatesGroupedBySubsection[subsection].map(subField => subField.key);
                                  const sortedFields = [...endData].sort(dynamicSort(keys) as (a: RowData, b: RowData) => number);
                                  setEndData( direction === 'asc' ? [...sortedFields] : [...sortedFields].reverse());
                                  setCurrentPage(1);
                                  setSearch('');
                                  setCheckedRows([]);
                                  setItemsPerPage(25);
                                  setModalOpen(undefined);
                                  setRemovedItemsModalOpen(false);
                                }
                                setCurrentSortDirection({
                                  column: columnId,
                                  direction,
                                });
                              },
                            }
                            : undefined}>
                          <span className="caption" style={{ color: 'var(--color-gray-75)', textAlign: 'left' }}>
                            {subsection}
                          </span>
                        </TH>
                      ))}
                    </>
                    : <>
                      {(fullField.listItemTemplates ?? [])
                        .filter(subField => subField.isViewableInTableView)
                        .map(subField => (
                          <TH
                            key={subField.id}
                            columnId={subField.key}
                            sort={subField.isViewableInTableView
                              ? {
                                currentSortByField: currentSortDirection?.column || '',
                                currentSortDirection: currentSortDirection?.direction ?? 'asc',
                                onSort(columnId, direction) {
                                  const sortedFields = [...endData].sort(dynamicSort([subField.key]) as (a: RowData, b: RowData) => number);
                                  setEndData(direction === 'asc' ? [...sortedFields] : [...sortedFields].reverse());
                                  setCurrentPage(1);
                                  setSearch('');
                                  setCheckedRows([]);
                                  setItemsPerPage(25);
                                  setModalOpen(undefined);
                                  setRemovedItemsModalOpen(false);
                                  setCurrentSortDirection({
                                    column: columnId,
                                    direction,
                                  });
                                },
                              }
                              : undefined}
                          >
                            <span className="caption" style={{ color: 'var(--color-gray-75)', textAlign: 'left' }}>
                              {subField.label}
                            </span>
                          </TH>
                        ))}
                    </>}
                  {foundFields.length > 0 && !isPreview && !fullField.disabled && <TH />}
                </TableRow>
              </THead>
              <>
                {foundFields.length > 0
                  ? (foundFields
                    .map(item => {
                      if (item._associatedWithId) {
                        return null;
                      }
                      const associatedFields = foundFields
                        .filter((subField) => subField
                          ._associatedWithId === item._id);
                      if (associatedFields.length > 0) {
                        return (
                          <TBody key={item._id} className={associatedFields.length > 0 ? s.associated : ''}>
                            {renderTableRow(item, undefined, listSubsections)}
                            {showDuplicates
                              ? associatedFields.map((associatedField) => renderTableRow(associatedField, item._id, listSubsections, true))
                              : null}
                          </TBody>
                        );
                      }
                      return (
                        <TBody key={item._id}>
                          {renderTableRow(item, undefined, listSubsections)}
                        </TBody>
                      );
                    })
                  )
                  : (
                    <TBody>
                      <TableRow>
                        <TD colSpan={(fullField.listItemTemplates ?? []).length + 1} label="No items found" />
                      </TableRow>
                    </TBody>
                  )}
              </>
            </Table>
            <div className={s.tableFooter}>
              <div className={s.tableFooterButtons}>
                {!isPreview && !fullField.disabled && <button className={s.addItemButton} onClick={() => {
                  append(initializeSubFields());
                  if (foundFields.length >= itemsPerPage) {
                    setCurrentPage(currentPage + 1);
                  }
                  setModalOpen(fields.length);
                }}>
                  <PlusCircle weight="fill" /> Add Item
                </button>}
                {checkedRows.length > 0 && (
                  <button className={s.deleteButton} onClick={removeSelectedItems}>
                    <MinusCircle weight="fill" /> Remove {checkedRows.length} selected
                  </button>
                )}
              </div>
              {!search && <>
                <PaginationControls
                  page={currentPage}
                  numPages={Math.ceil(endData.length / itemsPerPage)}
                  onChangePage={setCurrentPage}
                  hideArrows
                  className={s.pagination}
                />
                <CustomSelect
                  native
                  value={String(itemsPerPage)}
                  id="itemsPerPage"
                  options={PAGINATION_OPTIONS}
                  onChange={(value) => {
                    const newItemsPerPage = Number(value) as 25 | 50 | 100;
                    setItemsPerPage(newItemsPerPage);
                    setCurrentPage(1);
                  }}
                  className={s.pagesSelect}
                />
              </>}
            </div>
          </div>
        )
        : (
          <>
            {isPreview && !isEditor && fullField.listItemTemplates && (
              <div className={s.listItem}>{fullField.listItemTemplates.map((previewField, index) => (
                <NonListFormField
                  key={previewField.id}
                  listField={fullField}
                  index={index}
                  field={{
                    ...previewField,
                    key: `${linkSourceFieldKey}[${index}].${previewField.key}`,
                    linkedFields: fieldsByKey[previewField.key]?.linkedFields?.filter((f) => f.linkedFieldTemplateIndex === index),
                    listItemTemplates: Object.values(fieldsByKey).filter((f) => f.listFieldTemplateId === previewField.id),
                  }}
                  control={control}
                  rules={rules[previewField.key]}
                  isPreview={isPreview}
                  fieldsAutofilling={fieldsAutofilling}
                />
              ))}
              </div>
            )}
            {fields.map((item, index) => (
              <div key={item._id} className={s.listItem}>
                {(fullField?.listItemTemplates ?? [])
                  .map(subField => (
                    <NonListFormField
                      key={subField.id}
                      listField={fullField}
                      index={index}
                      field={{
                        ...subField,
                        key: `${linkSourceFieldKey}[${index}].${subField.key}`,
                        linkedFields: fieldsByKey[subField.key]?.linkedFields?.filter((f) => f.linkedFieldTemplateIndex === index),
                        listItemTemplates: Object.values(fieldsByKey).filter((f) => f.listFieldTemplateId === subField.id),
                      }}
                      control={control}
                      rules={rules[subField.key]}
                      isPreview={isPreview}
                      fieldsAutofilling={fieldsAutofilling}
                    />
                  ))}
                {!isPreview && inMainList && !fullField.disabled && (
                  <button
                    className={s.removeItemButton}
                    type="button"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to remove this item?')) {
                        removeListItem(index);
                      }
                    }}
                  >
                    <MinusCircle weight="fill" /> Remove Item
                  </button>
                )}
              </div>
            ))}
            {!isPreview && !fullField.disabled && (
              <button className={s.addItemButton} onClick={() => append(initializeSubFields())}>
                <PlusCircle weight="fill" /> Add Item
              </button>
            )}
          </>
        )}
      {modalOpen !== undefined && (
        questionnaireEditListItemOnSave
          ? (
            <EditListItemModalOnSave
              field={fullField}
              setIndex={setModalOpen}
              isPreview={isPreview ?? false}
              removeListItem={removeListItem}
              index={modalOpen}
              linkSourceFieldKey={linkSourceFieldKey}
              control={control}
              FormFieldComponent={NonListFormField}
              fieldsAutofilling={fieldsAutofilling}
            />
          )
          : (
            <EditListItemModal
              field={fullField}
              setIndex={setModalOpen}
              isPreview={isPreview ?? false}
              removeListItem={removeListItem}
              index={modalOpen}
              linkSourceFieldKey={linkSourceFieldKey}
              control={control}
              FormFieldComponent={NonListFormField}
              fieldsAutofilling={fieldsAutofilling}
            />
          )
      )}
      {removedItemsModalOpen && <DeletedFieldsModal
        fullField={fullField}
        deletedValues={deletedValues}
        setRemovedItemsModalOpen={setRemovedItemsModalOpen}
        questionnaireId={questionnaireId}
        setValue={setValue}
        formMethods={formMethods}
        linkSourceFieldKey={linkSourceFieldKey}
      />}
    </div>
  );
};

export default List;
